import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  Button,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { createRoyalties, deleteRoyalties, getRoyalties, updateRoyalties } from "../../api/royalty";
import { customCurrencyFormat } from "../../utils";


const CustomerRoyaltyFees = ({ customerId }) => {
  const [royaltyFees, setRoyaltyFees] = useState([]);
  const [name, setName] = useState("");
  const [royaltyType, setRoyaltyType] = useState("percentage");
  const [value, setValue] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    royalty_type: "",
    value: "",
  });

  const toast = useToast();

  // fetch fees
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getRoyalties(customerId);
        setRoyaltyFees(res);
      } catch (err) {
        console.error("Error fetching royalty fees:", err);
      }
    };
    fetchData();
  }, [customerId]);

  const handleAdd = async () => {
    if (!name.trim()) {
      toast({ title: "Name is required", status: "error", duration: 3000 });
      return;
    }
    if (!royaltyType) {
      toast({
        title: "Royalty type is required",
        status: "error",
        duration: 3000,
      });
      return;
    }
    if (!value || isNaN(value)) {
      toast({
        title: "Value must be a number",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const res = await createRoyalties(customerId, {
        customer: customerId,
        name,
        royalty_type: royaltyType,
        value,
      });

      toast({ title: "Royalty fee added", status: "success", duration: 3000 });

      setRoyaltyFees((prev) => [...prev, res.data]);

      setName("");
      setRoyaltyType("percentage");
      setValue("");
    } catch (err) {
      toast({
        title: "Failed to add royalty fee",
        description: err.response?.data?.detail || "An error occurred",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleEdit = (fee) => {
    setEditingId(fee.id);
    setEditForm({
      name: fee.name,
      royalty_type: fee.royalty_type,
      value: fee.value,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({ name: "", royalty_type: "", value: "" });
  };

  const handleSave = async (id) => {
    try {
      const res = await updateRoyalties(customerId, id, editForm);
      setRoyaltyFees((prev) =>
        prev.map((fee) => (fee.id === id ? res.data : fee))
      );
      setEditingId(null);
      setEditForm({ name: "", royalty_type: "", value: "" });
    } catch (err) {
      console.error("Error updating royalty fee:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRoyalties(customerId, id);
      setRoyaltyFees((prev) => prev.filter((fee) => fee.id !== id));
    } catch (err) {
      console.error("Error deleting royalty fee:", err);
    }
  };

  return (
    <Box p={4} backgroundColor={"white"} h={"100%"}>
      <Heading fontSize="xl" mb={4}>
        Customer Royalty Fees
      </Heading>

      <HStack spacing={3} mb={4} align="flex-end">
        <Input
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="sm"
        />
        <Select
          value={royaltyType}
          onChange={(e) => setRoyaltyType(e.target.value)}
          size="sm"
        >
          <option value="percentage">Percentage</option>
          <option value="flat">Flat</option>
        </Select>
        <Input
          placeholder="Value"
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="sm"
        />
        <Button onClick={handleAdd} colorScheme="blue" size="sm" p={4}>
          Add
        </Button>
      </HStack>

      <Table size="md" variant="simple">
        <Thead fontSize={"20px"} p={"10px"}>
          <Tr>
            <Th>Name</Th>
            <Th>Type</Th>
            <Th>Value</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {royaltyFees.map((fee) => (
            <Tr key={fee.id}>
              <Td>
                {editingId === fee.id ? (
                  <Input
                    size="sm"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                ) : (
                  fee.name
                )}
              </Td>
              <Td>
                {editingId === fee.id ? (
                  <Select
                    size="sm"
                    value={editForm.royalty_type}
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        royalty_type: e.target.value,
                      }))
                    }
                  >
                    <option value="percentage">Percentage</option>
                    <option value="flat">Flat</option>
                  </Select>
                ) : (
                  fee.royalty_type
                )}
              </Td>
              <Td>
                {editingId === fee.id ? (
                  <Input
                    size="sm"
                    type="number"
                    value={editForm.value}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, value: e.target.value }))
                    }
                  />
                ) : (
                  customCurrencyFormat(
                    fee.value,
                    fee.royalty_type === "percentage"
                  )
                )}
              </Td>
              <Td>
                {editingId === fee.id ? (
                  <HStack>
                    <Button
                      size="xs"
                      colorScheme="green"
                      onClick={() => handleSave(fee.id)}
                    >
                      Save
                    </Button>
                    <Button size="xs" colorScheme="gray" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </HStack>
                ) : (
                  <HStack>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      onClick={() => handleEdit(fee)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="red"
                      onClick={() => handleDelete(fee.id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default CustomerRoyaltyFees;